/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@chakra-ui/react";
import SweetAlert2 from "react-sweetalert2";
import { FaAngleLeft } from "react-icons/fa";
import { Tooltip } from "@mui/material";
import InputField from "components/fields/InputField";
import RoleDropdown from "views/admin/user/components/dropdown/RoleDropdown";
import MerchantListModal from "../shared/MerchantListModal";
import SubaccountListModal from "../shared/SubaccountListModal";
import AgentListModal from "../shared/AgentListModal";

const CreateUser = () => {
  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const currentUserRole = localStorage.getItem("userRole");
  const sysUsername = localStorage.getItem("username")?.toLowerCase();

  const [newUser, setNewUser] = useState<any>({
    username: "",
    email: "",
    role: "",
    merchant: "",
    subaccount: "",
    agent: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRoleLoading, setIsRoleLoading] = useState(true);
  const [roleOptions, setRoleOptions] = useState<string[]>([]);
  const [swalProps, setSwalProps] = useState<any>({});

  // Modal open/close state
  const [isMerchantModalOpen, setIsMerchantModalOpen] = useState<boolean>(false);
  const [isSubaccountModalOpen, setIsSubaccountModalOpen] = useState<boolean>(false);
  const [isAgentModalOpen, setIsAgentModalOpen] = useState<boolean>(false);

  // Selected entity state
  const [selectedMerchant, setSelectedMerchant] = useState<any>(null);
  const [selectedSubaccount, setSelectedSubaccount] = useState<any>(null);
  const [selectedAgent, setSelectedAgent] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsRoleLoading(true);
      const fetchedRoles = await getRoleOptions();
      setRoleOptions(fetchedRoles);
      setIsRoleLoading(false);
    };
    fetchData();
  }, []);

  const getRoleOptions = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/user/get-all-hierarchy-levels`
      );
      const roles = response.data;

      const currentRole = roles.find(
        (role: any) => role.name === currentUserRole
      );

      if (!currentRole) {
        return ["-- Please Select --"];
      }

      const roleOptionsArray = ["-- Please Select --"];
      roles.forEach((role: any) => {
        if (role.hierarchy_level !== 3) {
          roleOptionsArray.push(role.name);
        }
      });

      return roleOptionsArray;
    } catch (error) {
      console.error("Error fetching role options:", error);
      return [];
    }
  };

  // Select/clear handlers
  const handleMerchantSelect = (merchantData: any) => {
    setSelectedMerchant(merchantData);
    setSelectedSubaccount(null);
    setNewUser((prev: any) => ({ ...prev, merchant: merchantData.id, subaccount: "" }));
  };

  const handleClearMerchant = () => {
    setSelectedMerchant(null);
    setSelectedSubaccount(null);
    setNewUser((prev: any) => ({ ...prev, merchant: "", subaccount: "" }));
  };

  const handleSubaccountSelect = (subaccountData: any) => {
    setSelectedSubaccount(subaccountData);
    setNewUser((prev: any) => ({ ...prev, subaccount: subaccountData.id }));
  };

  const handleClearSubaccount = () => {
    setSelectedSubaccount(null);
    setNewUser((prev: any) => ({ ...prev, subaccount: "" }));
  };

  const handleAgentSelect = (agentData: any) => {
    setSelectedAgent(agentData);
    setNewUser((prev: any) => ({ ...prev, agent: agentData.id }));
  };

  const handleClearAgent = () => {
    setSelectedAgent(null);
    setNewUser((prev: any) => ({ ...prev, agent: "" }));
  };

  const handleInputChange = async (event: any) => {
    const { name, value } = event.target;

    let updatedUser = { ...newUser, [name]: value };
    if (name === "role") {
      // Reset all entity selections on role change
      setSelectedMerchant(null);
      setSelectedSubaccount(null);
      setSelectedAgent(null);
      updatedUser = {
        ...updatedUser,
        merchant: "",
        subaccount: "",
        agent: "",
      };
    }

    setNewUser(updatedUser);
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const isFormValid =
    newUser.username &&
    newUser.email &&
    isValidEmail(newUser.email) &&
    newUser.role &&
    (
      newUser.role === "SuperAdmin" ||
      (newUser.role === "Agent" && newUser.agent) ||
      (newUser.role === "Merchant" && newUser.merchant) ||
      (newUser.role !== "SuperAdmin" && newUser.role !== "Agent" && newUser.role !== "Merchant" && newUser.merchant && newUser.subaccount)
    );

  // Convert PascalCase/camelCase to kebab-case (e.g., SuperAdmin → super-admin)
  const toKebabCase = (str: string) => {
    return str.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
  };

  const handleSubmit = async () => {
    const { username, email, role, merchant, subaccount, agent } = newUser;

    const usernameRegex = /^[a-z0-9]+$/;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!usernameRegex.test(username)) {
      setSwalProps({
        show: true,
        icon: "error",
        title: "Oops!",
        html: "Username must contain only letters (a-z) with no spaces or special characters.",
        showConfirmButton: true,
        didClose: () => {
          setSwalProps({});
        },
      });
      return;
    }

    if (!emailRegex.test(email)) {
      setSwalProps({
        show: true,
        icon: "error",
        title: "Oops!",
        html: "Please enter a valid email address (e.g., user@example.com).",
        showConfirmButton: true,
        didClose: () => {
          setSwalProps({});
        },
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const postData: any = {
        username,
        email,
        role,
        merchantId: merchant,
        agentId: agent,
        userRole,
        sysUsername,
      };
      if (role !== "Merchant") {
        postData.subaccountId = subaccount;
      }

      const response = await axios.post(
        process.env.REACT_APP_API_URL +
          `/api/${userRole}/user/action/add-${toKebabCase(role)}`,
        postData
      );

      const createdUserId = response.data?.user?.userId;

      setSwalProps({
        show: true,
        icon: "success",
        title: "Success",
        html: "User successfully created!",
        showConfirmButton: true,
        didClose: () => {
          setSwalProps({});
          setIsSubmitting(false);
          if (createdUserId) {
            navigate(`/${userRole}/user-management/${createdUserId}`);
          } else {
            navigate(`/${userRole}/user-management`);
          }
        },
      });
    } catch (error: any) {
      setSwalProps({
        show: true,
        icon: "error",
        title: "Oops!",
        html: error.response?.data?.message
          ? error.response.data.message
          : "Failed to create new user, please try again.",
        showConfirmButton: true,
        didClose: () => {
          setSwalProps({});
          setIsSubmitting(false);
        },
      });
    }
  };

  const roleSelected = newUser.role && newUser.role !== "-- Please Select --";
  const showMerchant = roleSelected && newUser.role !== "Agent" && newUser.role !== "SuperAdmin";
  const showSubaccount = showMerchant && newUser.role !== "Merchant";
  const showAgent = roleSelected && newUser.role === "Agent";

  return (
    <>
      <form>
        <div className="inter mx-auto max-w-3xl space-y-6 rounded-xl bg-white p-12 shadow-lg ring-1 ring-gray-100">
          <div>
            <Button
              type="button"
              onClick={() => navigate(`/${userRole}/user-management`)}
              isDisabled={isSubmitting}
              className="
                mb-1 inline-flex items-center gap-1
                text-sm font-medium text-gray-700
                transition hover:text-gray-700
                disabled:cursor-not-allowed disabled:opacity-40
                dark:text-gray-400 dark:hover:text-white
              "
            >
              <FaAngleLeft className="text-base" />
              <span>User Management</span>
            </Button>

            <h2 className="text-left text-2xl font-bold text-navy-700">
              Create User
            </h2>
          </div>

          <div className="space-y-4 border-b border-gray-200 pb-8">
            <p className="text-md font-medium tracking-normal text-gray-700">
              Select user role
            </p>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                Role <span className="text-red-500">*</span>
              </label>
              <RoleDropdown
                isDisabled={isSubmitting || isRoleLoading}
                isLoading={isRoleLoading}
                newUser={newUser}
                handleInputChange={handleInputChange}
                roleOptions={roleOptions}
              />
            </div>

            {showMerchant && (
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                  Merchant <span className="text-red-500">*</span>
                </label>
                <Tooltip
                  title={selectedMerchant ? selectedMerchant.name : "Please Select"}
                  arrow
                  placement="top"
                >
                  <div
                    onClick={() => !isSubmitting && setIsMerchantModalOpen(true)}
                    className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                      isSubmitting
                        ? "cursor-not-allowed bg-disabledBg text-gray-700 font-medium py-[11px]"
                        : "cursor-pointer bg-white text-navy-700 hover:bg-gray-50 border border-gray-300 shadow-sm font-medium py-2.5"
                    }`}
                  >
                    <span className="truncate">
                      {selectedMerchant ? selectedMerchant.name : "Please Select"}
                    </span>
                    {selectedMerchant && !isSubmitting && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleClearMerchant(); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </Tooltip>
              </div>
            )}

            {showSubaccount && (
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                  Subaccount <span className="text-red-500">*</span>
                </label>
                <Tooltip
                  title={
                    !selectedMerchant
                      ? "Please select a merchant first"
                      : selectedSubaccount
                        ? selectedSubaccount.suba_name
                        : "Please Select"
                  }
                  arrow
                  placement="top"
                >
                  <div
                    onClick={() => !isSubmitting && selectedMerchant && setIsSubaccountModalOpen(true)}
                    className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                      isSubmitting || !selectedMerchant
                        ? "cursor-not-allowed bg-disabledBg text-gray-700 font-medium py-[11px]"
                        : "cursor-pointer bg-white text-navy-700 hover:bg-gray-50 border border-gray-300 shadow-sm font-medium py-2.5"
                    }`}
                  >
                    <span className="truncate">
                      {selectedSubaccount ? selectedSubaccount.suba_name : "Please Select"}
                    </span>
                    {selectedSubaccount && !isSubmitting && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleClearSubaccount(); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </Tooltip>
              </div>
            )}

            {showAgent && (
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                  Agent <span className="text-red-500">*</span>
                </label>
                <Tooltip
                  title={selectedAgent ? selectedAgent.suba_name : "Please Select"}
                  arrow
                  placement="top"
                >
                  <div
                    onClick={() => !isSubmitting && setIsAgentModalOpen(true)}
                    className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                      isSubmitting
                        ? "cursor-not-allowed bg-disabledBg text-gray-700 font-medium py-[11px]"
                        : "cursor-pointer bg-white text-navy-700 hover:bg-gray-50 border border-gray-300 shadow-sm font-medium py-2.5"
                    }`}
                  >
                    <span className="truncate">
                      {selectedAgent ? selectedAgent.suba_name : "Please Select"}
                    </span>
                    {selectedAgent && !isSubmitting && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleClearAgent(); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </Tooltip>
              </div>
            )}
          </div>

          {roleSelected && (
            <div className="space-y-4 border-b border-gray-200 pb-8">
              <p className="text-md font-medium tracking-normal text-gray-700">
                Provide user information
              </p>
              <div className="grid grid-cols-1 gap-8 gap-y-4 md:grid-cols-2">
                <div>
                  <InputField
                    disabled={isSubmitting}
                    variant="auth"
                    extra="shadow-sm"
                    label="Username"
                    required={true}
                    id="username"
                    name="username"
                    type="text"
                    placeholder="Enter username"
                    value={newUser.username}
                    onChange={handleInputChange}
                    autoComplete="off"
                  />
                </div>

                <div>
                  <InputField
                    disabled={isSubmitting}
                    variant="auth"
                    extra="shadow-sm"
                    label="Email"
                    required={true}
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Enter email"
                    value={newUser.email}
                    onChange={handleInputChange}
                    autoComplete="off"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="md:text-right">
            <Button
              onClick={() => navigate(`/${userRole}/user-management`)}
              isDisabled={isSubmitting}
              className="mr-4 rounded-md border border-gray-300 bg-white px-8 py-2 text-sm font-semibold tracking-tight text-gray-700 shadow-sm transition hover:bg-gray-100 hover:text-gray-900 active:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/20 dark:bg-white/10 dark:text-white dark:hover:bg-white/20 md:px-10"
            >
              Back
            </Button>
            <Button
              onClick={() => handleSubmit()}
              isDisabled={!isFormValid || isSubmitting}
              isLoading={isSubmitting}
              loadingText="Creating..."
              className="btn-primary relative overflow-hidden rounded-md px-8 py-2.5 font-semibold text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 md:px-10"
            >
              <span className="relative z-0 tracking-tight">
                Create User
              </span>
            </Button>
          </div>
        </div>
      </form>

      {/* Entity Selection Modals */}
      {isMerchantModalOpen && (
        <MerchantListModal
          isOpen={isMerchantModalOpen}
          onClose={() => setIsMerchantModalOpen(false)}
          onSelect={handleMerchantSelect}
        />
      )}

      {isSubaccountModalOpen && selectedMerchant && (
        <SubaccountListModal
          isOpen={isSubaccountModalOpen}
          onClose={() => setIsSubaccountModalOpen(false)}
          onSelect={handleSubaccountSelect}
          merchantId={selectedMerchant.id}
        />
      )}

      {isAgentModalOpen && (
        <AgentListModal
          isOpen={isAgentModalOpen}
          onClose={() => setIsAgentModalOpen(false)}
          onSelect={handleAgentSelect}
        />
      )}

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      />
    </>
  );
};

export default CreateUser;
